import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { EnterpriseSearchService } from '@sabstravtech/obtservices/angular';
import { NamedPassport } from '@sabstravtech/obtservices/base';

import { ChatWidgetInputBase } from '../chat-widget-input-base';

export interface ChatPassportFields {
  documentId?: string;
  passportNumber?: string;
  passportCountry?: string;
  dateOfBirth?: string;
  dateOfIssue?: string;
  dateOfExpiry?: string;
  gender?: string | null;
  nationality?: string;
  codeOfIssuingState?: string;
}

export type ChatPassportAnswer =
  | ({ kind: 'saved'; description: string } & ChatPassportFields)
  | ({ kind: 'manual' } & ChatPassportFields);

/** Sentinel dropdown value for the explicit "None" option. */
const NONE_ID = '__none__';

/**
 * Renders the passport picker widget inside the chat widget: a dropdown of
 * the passenger's saved passports (only shown when there are any) plus a
 * "None" option, and manual entry fields for every sub-part (passport
 * number, date of birth, date of issue, expiry date, gender, nationality,
 * issuing state code) shown whenever no saved passport is selected. Mirrors
 * the phone-number widget's logic exactly (including the locked/read-only
 * behaviour once an earlier widget instance is superseded).
 */
@Component({
  selector: 'app-chat-passport-input',
  standalone: false,
  templateUrl: './chat-passport-input.component.html',
  styleUrls: ['./chat-passport-input.component.scss']
})
export class ChatPassportInputComponent extends ChatWidgetInputBase implements OnChanges {
  @Input() passengerName = '';
  @Input() savedPassports: NamedPassport[] = [];
  @Input() passportFields: string[] | undefined;
  @Input() required?: boolean;
  @Output() answer = new EventEmitter<ChatPassportAnswer>();
  @Output() skip = new EventEmitter<void>();

  readonly NONE_ID = NONE_ID;

  selectedId = '';
  passportNumber = '';
  passportCountry = '';
  dateOfBirth = '';
  dateOfIssue = '';
  dateOfExpiry = '';
  gender: 'M' | 'F' | null = null;
  nationality = '';
  codeOfIssuingState = '';
  /** Human-readable summary of what was submitted (via Continue or Skip),
   * shown in place of the controls once this widget instance is locked. */
  chosenSummary: string | null = null;

  constructor(public searchService: EnterpriseSearchService) {
    super();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Reset the picker whenever the passenger/savedPassports change (new
    // question) — but NOT merely because `locked` toggled, which would wipe
    // out the selection just as we want to display it as read-only.
    if (this.onlyLockedChanged(changes)) {
      return;
    }
    this.selectedId = this.savedPassports.length ? String(this.savedPassports[0].id) : '';
    this.passportNumber = '';
    this.passportCountry = '';
    this.dateOfBirth = '';
    this.dateOfIssue = '';
    this.dateOfExpiry = '';
    this.gender = null;
    this.nationality = '';
    this.codeOfIssuingState = '';
    this.chosenSummary = null;
  }

  get isMandatory(): boolean {
    return this.required === true;
  }

  get hasSavedPassports(): boolean {
    return this.savedPassports.length > 0;
  }

  get selectedSavedPassport(): NamedPassport | null {
    if (!this.selectedId || this.selectedId === NONE_ID) {
      return null;
    }
    // Native <select> option values are always strings, but saved.id may come
    // back from the backend as a number — compare as strings on both sides so
    // a valid selection is never silently treated as "nothing chosen".
    return this.savedPassports.find((p) => String(p.id) === this.selectedId) ?? null;
  }

  get showManualEntry(): boolean {
    return !this.hasSavedPassports || this.selectedId === NONE_ID;
  }

  hasField(field: string): boolean {
    return !this.passportFields || this.passportFields.includes(field);
  }

  private get manualFieldsComplete(): boolean {
    const values: Record<string, unknown> = {
      passportNumber: this.passportNumber,
      passportCountry: this.passportCountry,
      dateOfBirth: this.dateOfBirth,
      dateOfIssue: this.dateOfIssue,
      dateOfExpiry: this.dateOfExpiry,
      gender: this.gender,
      nationality: this.nationality,
      codeOfIssuingState: this.codeOfIssuingState
    };
    return (this.passportFields ?? Object.keys(values)).every((field) => {
      const value = values[field];
      return typeof value === 'string' ? !!value.trim() : !!value;
    });
  }

  get isValid(): boolean {
    if (this.selectedSavedPassport) {
      return true;
    }
    if (this.manualFieldsComplete) {
      return true;
    }
    return !this.isMandatory;
  }

  formatSavedPassport(saved: NamedPassport): string {
    const label = saved.nationality ? `${saved.nationality} Passport` : 'Passport';
    return saved.number ? `${label} - ${saved.number}` : label;
  }

  /** Angular templates can't call the global String() directly. */
  idToString(id: string | number): string {
    return String(id);
  }

  onDropdownChange(id: string): void {
    if (this.locked) {
      return;
    }
    this.selectedId = id;
  }

  onContinue(): void {
    if (this.locked || !this.isValid) {
      return;
    }
    const saved = this.selectedSavedPassport;
    if (saved) {
      const fields: ChatPassportFields = {
        documentId: String(saved.id)
      };
      this.chosenSummary = this.formatSavedPassport(saved);
      this.answer.emit({ kind: 'saved', description: this.chosenSummary, ...fields });
      return;
    }
    if (this.manualFieldsComplete) {
      const fields: ChatPassportFields = {
        passportNumber: this.passportNumber.trim(),
        passportCountry: this.passportCountry,
        dateOfBirth: this.dateOfBirth,
        dateOfIssue: this.dateOfIssue,
        dateOfExpiry: this.dateOfExpiry,
        gender: this.gender as 'M' | 'F',
        nationality: this.nationality.trim(),
        codeOfIssuingState: this.codeOfIssuingState.trim().toUpperCase()
      };
      this.chosenSummary = `${fields.nationality} Passport - ${fields.passportNumber}`;
      this.answer.emit({ kind: 'manual', ...fields });
      return;
    }
    // Only reachable for an optional field with nothing entered.
    this.chosenSummary = 'Skipped';
    this.skip.emit();
  }

  onSkip(): void {
    if (this.locked) {
      return;
    }
    this.chosenSummary = 'Skipped';
    this.skip.emit();
  }
}
